(function () {
  const config = window.ZSearchConfig || {};
  const settings = config.settings || {};
  const redirects = config.redirects || {};
  const synonyms = config.synonyms || {};
  const replacements = config.replacements || [];
  const exactQueryRewrites = config.exactQueryRewrites || {};
  const collectionIntentRules = config.collectionIntentRules || [];
  const predictiveConfig = config.predictive || {};
  const vendors = window.ZSearchVendors || [];

  const DEBUG = !!settings.debug;
  const MIN_PREDICTIVE_CHARS = settings.minPredictiveChars || 2;
  const PREDICTIVE_DEBOUNCE_MS = settings.predictiveDebounceMs || 220;
  const SIZE_FILTER_PARAM = "filter.v.m.custom.primary_size";
  const GENDER_FILTER_PARAM = "filter.p.m.my_fields.gender";

  let predictiveAbortController = null;

  function log() {
    if (!DEBUG) return;
    console.log("[z-search]", ...arguments);
  }

  function normalizeQuery(raw) {
    let q = String(raw || "").toLowerCase();

    replacements.forEach(function (rule) {
      try {
        q = q.replace(new RegExp(rule.pattern, rule.flags || ""), rule.replace);
      } catch (e) {
        log("Replacement rule failed", rule, e);
      }
    });

    if (synonyms[q]) q = synonyms[q];
    if (exactQueryRewrites[q]) q = exactQueryRewrites[q];

    return q;
  }

  function getQueryVariants(normalizedQuery) {
    const variants = [];
    const q = String(normalizedQuery || "").trim();

    if (!q) return variants;

    variants.push(q);

    if (q.endsWith("s") && q.length > 1) {
      variants.push(q.slice(0, -1));
    }

    return Array.from(new Set(variants));
  }

  function detectVendorRedirect(queryVariants) {
    if (!vendors || !vendors.length) return null;

    for (let i = 0; i < queryVariants.length; i++) {
      const query = queryVariants[i];

      for (let j = 0; j < vendors.length; j++) {
        const vendor = vendors[j];

        if (query === vendor) {
          return "/collections/" + vendor.replace(/\s+/g, "-");
        }
      }
    }

    return null;
  }

  function ruleMatches(query, rule) {
    if (!rule || !rule.matchType || !rule.query || !rule.redirect) {
      return false;
    }

    if (rule.matchType === "exact") {
      return query === rule.query;
    }

    if (rule.matchType === "startsWith") {
      return query.indexOf(rule.query) === 0;
    }

    if (rule.matchType === "contains") {
      return query.indexOf(rule.query) !== -1;
    }

    return false;
  }

  function getRuleScore(rule) {
    if (!rule || !rule.matchType || !rule.query) return 0;

    if (rule.matchType === "exact") {
      return 3000 + rule.query.length;
    }

    if (rule.matchType === "startsWith") {
      return 2000 + rule.query.length;
    }

    if (rule.matchType === "contains") {
      return 1000 + rule.query.length;
    }

    return rule.query.length;
  }

  function extractSize(normalizedQuery) {
    const q = String(normalizedQuery || "").trim();
    if (!q) return null;

    const numericMatch = q.match(/(?:^|\b)(?:size\s*)?(3[5-9]|4[0-9]|50)(?=\b|$)/);
    if (numericMatch) {
      return numericMatch[1];
    }

    const alphaPatterns = [
      { regex: /(?:^|\b)(?:size\s*)?(xxxl|3xl)(?=\b|$)/, value: "3XL" },
      { regex: /(?:^|\b)(?:size\s*)?(xxl|2xl)(?=\b|$)/, value: "2XL" },
      { regex: /(?:^|\b)(?:size\s*)?(xl|x-large|extra large)(?=\b|$)/, value: "XL" },
      { regex: /(?:^|\b)(?:size\s*)?(large|lg|l)(?=\b|$)/, value: "L" },
      { regex: /(?:^|\b)(?:size\s*)?(medium|med|m)(?=\b|$)/, value: "M" },
      { regex: /(?:^|\b)(?:size\s*)?(small|sm|s)(?=\b|$)/, value: "S" },
      { regex: /(?:^|\b)(?:size\s*)?(xs|x-small|extra small)(?=\b|$)/, value: "XS" },
      { regex: /(?:^|\b)(?:size\s*)?(xxs|2xs)(?=\b|$)/, value: "2XS" }
    ];

    for (let i = 0; i < alphaPatterns.length; i++) {
      if (alphaPatterns[i].regex.test(q)) {
        return alphaPatterns[i].value;
      }
    }

    return null;
  }

  function stripSizeFromQuery(normalizedQuery) {
    let q = String(normalizedQuery || "");

    q = q.replace(/\bsize\s*(3[5-9]|4[0-9]|50)\b/g, " ");
    q = q.replace(/\b(3[5-9]|4[0-9]|50)\b/g, " ");

    q = q.replace(/\bsize\s*(xxxl|3xl|xxl|2xl|xl|x-large|extra large|large|lg|l|medium|med|m|small|sm|s|xs|x-small|extra small|xxs|2xs)\b/g, " ");
    q = q.replace(/\b(xxxl|3xl|xxl|2xl|xl|x-large|extra large|large|lg|l|medium|med|m|small|sm|s|xs|x-small|extra small|xxs|2xs)\b/g, " ");

    q = q.replace(/\s+/g, " ").trim();

    return q;
  }

  function extractGender(normalizedQuery) {
    const q = String(normalizedQuery || "").trim();
    if (!q) return null;

    const genderPatterns = [
      { regex: /(?:^|\b)(baby girls)(?=\b|$)/, value: "Baby Girls" },
      { regex: /(?:^|\b)(baby boys)(?=\b|$)/, value: "Baby Boys" },
      { regex: /(?:^|\b)(unisex kids|kids unisex)(?=\b|$)/, value: "Unisex Kids" },
      { regex: /(?:^|\b)(baby)(?=\b|$)/, value: "Baby" },
      { regex: /(?:^|\b)(juniors|junior)(?=\b|$)/, value: "Juniors" },
      { regex: /(?:^|\b)(womens|women's|women)(?=\b|$)/, value: "Womens" },
      { regex: /(?:^|\b)(mens|men's|men)(?=\b|$)/, value: "Mens" },
      { regex: /(?:^|\b)(girls|girl)(?=\b|$)/, value: "Girls" },
      { regex: /(?:^|\b)(boys|boy)(?=\b|$)/, value: "Boys" },
      { regex: /(?:^|\b)(unisex)(?=\b|$)/, value: "Unisex" }
    ];

    for (let i = 0; i < genderPatterns.length; i++) {
      if (genderPatterns[i].regex.test(q)) {
        return genderPatterns[i].value;
      }
    }

    return null;
  }

  function stripGenderFromQuery(normalizedQuery) {
    let q = String(normalizedQuery || "");

    q = q.replace(/\b(baby girls)\b/g, " ");
    q = q.replace(/\b(baby boys)\b/g, " ");
    q = q.replace(/\b(unisex kids|kids unisex)\b/g, " ");
    q = q.replace(/\b(juniors|junior)\b/g, " ");
    q = q.replace(/\b(womens|women's|women)\b/g, " ");
    q = q.replace(/\b(mens|men's|men)\b/g, " ");
    q = q.replace(/\b(girls|girl)\b/g, " ");
    q = q.replace(/\b(boys|boy)\b/g, " ");
    q = q.replace(/\b(unisex)\b/g, " ");
    q = q.replace(/\b(baby)\b/g, " ");

    q = q.replace(/\s+/g, " ").trim();

    return q;
  }

  function isAlphaSize(sizeValue) {
    return ["2XS", "XS", "S", "M", "L", "XL", "2XL", "3XL"].indexOf(sizeValue) !== -1;
  }

  function isNumericSize(sizeValue) {
    return /^\d+$/.test(String(sizeValue || ""));
  }

  function isSizeFilterEligibleRedirect(redirectUrl, baseQuery, sizeValue) {
    const eligiblePrefixes = [
      "/collections/birkenstock",
      "/collections/birkenstock-",
      "/collections/shoes",
      "/collections/womens-shoes",
      "/collections/mens-shoes",
      "/collections/sandals",
      "/collections/boots",
      "/collections/sneakers",
      "/collections/slides",
      "/collections/nike",
      "/collections/nike-shoes",
      "/collections/adidas",
      "/collections/adidas-shoes",
      "/collections/new-balance",
      "/collections/new-balance-shoes",
      "/collections/hoka",
      "/collections/asics",
      "/collections/puma",
      "/collections/reebok",
      "/collections/vionic",
      "/collections/brooks",
      "/collections/merrell",
      "/collections/crocs",
      "/collections/clarks",
      "/collections/spenco",
      "/collections/jbu",
      "/collections/ugg",
      "/collections/toms",
      "/collections/ryka",
      "/collections/franco-sarto",
      "/collections/fly-london",
      "/collections/vuori",
      "/collections/columbia"
    ];

    for (let i = 0; i < eligiblePrefixes.length; i++) {
      if (redirectUrl.indexOf(eligiblePrefixes[i]) === 0) {
        if (isNumericSize(sizeValue)) {
          return true;
        }

        if (isAlphaSize(sizeValue)) {
          const apparelKeywords = [
            "shirt",
            "shirts",
            "hoodie",
            "hoodies",
            "top",
            "tops",
            "jacket",
            "jackets",
            "pants",
            "shorts",
            "leggings",
            "sweater",
            "sweaters",
            "sweatshirt",
            "sweatshirts",
            "jogger",
            "joggers",
            "polo",
            "polos",
            "blouse",
            "blouses",
            "tunic",
            "tunics",
            "dress",
            "dresses",
            "clothing",
            "apparel"
          ];

          const footwearKeywords = [
            "shoe",
            "shoes",
            "sandal",
            "sandals",
            "boot",
            "boots",
            "sneaker",
            "sneakers",
            "slide",
            "slides",
            "clog",
            "clogs",
            "birkenstock",
            "arizona",
            "boston",
            "gizeh",
            "mayari",
            "madrid",
            "milano",
            "siena",
            "franca"
          ];

          const queryText = String(baseQuery || "");

          for (let j = 0; j < apparelKeywords.length; j++) {
            if (queryText.indexOf(apparelKeywords[j]) !== -1) {
              return true;
            }
          }

          for (let k = 0; k < footwearKeywords.length; k++) {
            if (queryText.indexOf(footwearKeywords[k]) !== -1) {
              return true;
            }
          }

          if (
            redirectUrl.indexOf("/collections/nike") === 0 ||
            redirectUrl.indexOf("/collections/adidas") === 0 ||
            redirectUrl.indexOf("/collections/new-balance") === 0 ||
            redirectUrl.indexOf("/collections/vuori") === 0 ||
            redirectUrl.indexOf("/collections/columbia") === 0
          ) {
            return true;
          }
        }
      }
    }

    return false;
  }

  function isGenderFilterEligibleRedirect(redirectUrl) {
    const eligiblePrefixes = [
      "/collections/birkenstock",
      "/collections/birkenstock-",
      "/collections/shoes",
      "/collections/womens-shoes",
      "/collections/mens-shoes",
      "/collections/sandals",
      "/collections/boots",
      "/collections/sneakers",
      "/collections/slides",
      "/collections/shirts",
      "/collections/womens-tops",
      "/collections/tunics",
      "/collections/pullovers",
      "/collections/fleece",
      "/collections/hoodies",
      "/collections/jackets",
      "/collections/dresses",
      "/collections/maxi-dresses",
      "/collections/plus-size-dresses",
      "/collections/pants",
      "/collections/shorts",
      "/collections/jeans",
      "/collections/leggings",
      "/collections/skirts",
      "/collections/sweatpants",
      "/collections/womens-clothing",
      "/collections/girls-clothing",
      "/collections/maternity",
      "/collections/nike",
      "/collections/nike-shoes",
      "/collections/adidas",
      "/collections/adidas-shoes",
      "/collections/new-balance",
      "/collections/new-balance-shoes",
      "/collections/hoka",
      "/collections/asics",
      "/collections/puma",
      "/collections/reebok",
      "/collections/vionic",
      "/collections/brooks",
      "/collections/merrell",
      "/collections/crocs",
      "/collections/clarks",
      "/collections/spenco",
      "/collections/jbu",
      "/collections/ugg",
      "/collections/toms",
      "/collections/ryka",
      "/collections/franco-sarto",
      "/collections/fly-london",
      "/collections/vuori",
      "/collections/columbia"
    ];

    for (let i = 0; i < eligiblePrefixes.length; i++) {
      if (redirectUrl.indexOf(eligiblePrefixes[i]) === 0) {
        return true;
      }
    }

    return false;
  }

  function appendFilters(redirectUrl, size, gender) {
    const url = new URL(redirectUrl, window.location.origin);

    if (!url.searchParams.has("sort_by")) {
      url.searchParams.set("sort_by", "best-selling");
    }

    if (gender) {
      url.searchParams.set(GENDER_FILTER_PARAM, gender);
    }

    if (size) {
      url.searchParams.set(SIZE_FILTER_PARAM, size);
    }

    return url.pathname + "?" + url.searchParams.toString();
  }

  function findBaseRedirect(normalizedQuery) {
    const queryVariants = getQueryVariants(normalizedQuery);

    for (let i = 0; i < queryVariants.length; i++) {
      const variant = queryVariants[i];

      if (redirects[variant]) {
        return redirects[variant];
      }
    }

    const vendorRedirect = detectVendorRedirect(queryVariants);
    if (vendorRedirect) {
      return vendorRedirect;
    }

    const matchedRules = [];

    for (let i = 0; i < collectionIntentRules.length; i++) {
      const rule = collectionIntentRules[i];

      for (let j = 0; j < queryVariants.length; j++) {
        const variant = queryVariants[j];

        if (ruleMatches(variant, rule)) {
          matchedRules.push({
            rule: rule,
            score: getRuleScore(rule)
          });
          break;
        }
      }
    }

    if (matchedRules.length > 0) {
      matchedRules.sort(function (a, b) {
        return b.score - a.score;
      });

      return matchedRules[0].rule.redirect;
    }

    return null;
  }

  function findRedirect(normalizedQuery) {
    const size = extractSize(normalizedQuery);
    const gender = extractGender(normalizedQuery);

    let baseQuery = normalizedQuery;

    if (size) {
      baseQuery = stripSizeFromQuery(baseQuery);
    }

    if (gender) {
      baseQuery = stripGenderFromQuery(baseQuery);
    }

    const baseRedirect = findBaseRedirect(baseQuery);

    if (!baseRedirect) {
      return null;
    }

    const applySize = size && isSizeFilterEligibleRedirect(baseRedirect, baseQuery, size);
    const applyGender = gender && isGenderFilterEligibleRedirect(baseRedirect);

    if (applySize || applyGender) {
      return appendFilters(baseRedirect, applySize ? size : null, applyGender ? gender : null);
    }

    return baseRedirect;
  }

  function getSearchInputFromForm(form) {
    if (!form) return null;
    return form.querySelector('input[name="q"], input[type="search"]');
  }

  function isLikelySearchForm(form) {
    if (!form || form.tagName !== "FORM") return false;

    const action = form.getAttribute("action") || "";
    const hasQueryInput = !!form.querySelector('input[name="q"], input[type="search"]');

    return hasQueryInput && (
      action.indexOf("/search") !== -1 ||
      action === "" ||
      form.hasAttribute("data-predictive-search") ||
      form.className.toLowerCase().indexOf("search") !== -1 ||
      form.getAttribute("role") === "search"
    );
  }

  function getRedirectForRawQuery(rawQuery) {
    const normalizedQuery = normalizeQuery(rawQuery);
    const redirectUrl = findRedirect(normalizedQuery);

    return {
      normalizedQuery: normalizedQuery,
      redirectUrl: redirectUrl
    };
  }

  function tryRedirectFromForm(form, event) {
    if (!isLikelySearchForm(form)) return false;

    const queryInput = getSearchInputFromForm(form);
    if (!queryInput) return false;

    const result = getRedirectForRawQuery(queryInput.value);

    log("Form redirect check", {
      originalQuery: queryInput.value,
      normalizedQuery: result.normalizedQuery,
      redirectUrl: result.redirectUrl
    });

    queryInput.value = result.normalizedQuery;

    if (result.redirectUrl) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      window.location.assign(result.redirectUrl);
      return true;
    }

    ensureSearchTypeParams(form);
    return false;
  }

  function handleSearchSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== "FORM") return;

    tryRedirectFromForm(form, event);
  }

  function ensureSearchTypeParams(form) {
    const existingTypeInput = form.querySelector('input[name="type"]');
    if (!existingTypeInput) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "type";
      input.value = "product,collection";
      form.appendChild(input);
    }
  }

  function debounce(fn, wait) {
    let timeout;

    return function () {
      const context = this;
      const args = arguments;

      clearTimeout(timeout);

      timeout = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function fetchPredictiveSearch(query, callback) {
    if (!predictiveConfig.enabled) return;

    if (query.length < MIN_PREDICTIVE_CHARS) {
      callback(null, "");
      return;
    }

    if (predictiveAbortController) {
      predictiveAbortController.abort();
    }

    predictiveAbortController = new AbortController();

    const resourceTypes = (predictiveConfig.resourceTypes || ["product", "collection", "query"]).join(",");
    const limit = predictiveConfig.limit || 8;

    const url =
      "/search/suggest?q=" + encodeURIComponent(query) +
      "&resources[type]=" + encodeURIComponent(resourceTypes) +
      "&resources[limit]=" + encodeURIComponent(limit) +
      "&section_id=predictive-search";

    log("Predictive fetch", url);

    fetch(url, {
      signal: predictiveAbortController.signal,
      credentials: "same-origin"
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Predictive search request failed");
        return response.text();
      })
      .then(function (html) {
        callback(null, html);
      })
      .catch(function (error) {
        if (error.name === "AbortError") return;
        callback(error);
      });
  }

  function getPredictiveContainer(input) {
    const explicitTarget = input.getAttribute("data-predictive-target");
    if (explicitTarget) {
      return document.querySelector(explicitTarget);
    }

    const parent = input.closest("[data-search-root]");
    if (parent) {
      return parent.querySelector("[data-predictive-results]");
    }

    return document.querySelector("[data-predictive-results]");
  }

  function renderPredictiveResults(input, html) {
    const container = getPredictiveContainer(input);
    if (!container) return;

    if (!html) {
      container.innerHTML = "";
      container.hidden = true;
      return;
    }

    container.innerHTML = html;
    container.hidden = false;
  }

  const handlePredictiveInput = debounce(function (event) {
    const input = event.target;
    if (!input || (input.name !== "q" && input.type !== "search")) return;

    const form = input.closest("form");
    if (!isLikelySearchForm(form)) return;

    const normalizedQuery = normalizeQuery(input.value);
    const redirectUrl = findRedirect(normalizedQuery);

    log("Predictive input", { value: input.value, normalizedQuery, redirectUrl });

    if (redirectUrl && normalizedQuery.length >= MIN_PREDICTIVE_CHARS) {
      renderPredictiveResults(
        input,
        '<div class="z-search-redirect-suggestion"><a href="' +
          redirectUrl +
          '">Shop results for "<strong>' +
          escapeHtml(normalizedQuery) +
          '</strong>"</a></div>'
      );
      return;
    }

    fetchPredictiveSearch(normalizedQuery, function (error, html) {
      if (error) {
        log("Predictive search failed", error);
        return;
      }
      renderPredictiveResults(input, html);
    });
  }, PREDICTIVE_DEBOUNCE_MS);

  function handleEnterKey(event) {
    if (event.key !== "Enter") return;

    const target = event.target;
    if (!target) return;

    const isSearchInput =
      target.matches('input[name="q"]') ||
      target.matches('input[type="search"]');

    if (!isSearchInput) return;

    const form = target.closest("form");
    if (!form) return;

    tryRedirectFromForm(form, event);
  }

  function handleSearchButtonClick(event) {
    const button = event.target.closest('button[type="submit"], input[type="submit"]');
    if (!button) return;

    const form = button.closest("form");
    if (!form) return;

    tryRedirectFromForm(form, event);
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function init() {
    document.addEventListener("submit", handleSearchSubmit, true);
    document.addEventListener("input", handlePredictiveInput);
    document.addEventListener("keydown", handleEnterKey, true);
    document.addEventListener("click", handleSearchButtonClick, true);

    document.addEventListener("click", function (event) {
      const results = document.querySelector("[data-predictive-results]");
      if (!results) return;

      const searchRoot = event.target.closest("[data-search-root]");
      if (!searchRoot && !results.contains(event.target)) {
        results.hidden = true;
      }
    });

    log("Initialized");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();